import { useMemo } from 'react';

import { graphql } from 'react-relay';

import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';

import type {
	useNudgeFirstVisitFilterQuery$data,
	useNudgeFirstVisitFilterQuery$variables,
} from './__generated__/useNudgeFirstVisitFilterQuery.graphql.ts';

const useNudgeFirstVisitFilterQuery = graphql`
	query useNudgeFirstVisitFilterQuery($contentId: ID!, $contentType: CatchupContentType!) {
		catchupGetLastViewedTime(contentId: $contentId, contentType: $contentType) {
			lastViewedTimeISO
		}
	}
`;

type UseNudgeFirstVisitFilterResult = {
	isFirstVisit: boolean;
	loading: boolean;
};

/**
 * Pure function that derives isFirstVisit from a lastViewedTimeISO string.
 * Returns true when the string is absent or not a valid date.
 */
export function deriveIsFirstVisit(lastViewedTimeISO: string | null | undefined): boolean {
	if (!lastViewedTimeISO) {
		return true;
	}
	// Strip timezone bracket suffixes (e.g. "[America/New_York]") that cause NaN on Date.parse
	const cleaned = lastViewedTimeISO.replace(/\[.*?\]$/, '');
	return !cleaned || Number.isNaN(Date.parse(cleaned));
}

/**
 * Hook to check if the current user is visiting the given content for the first time.
 * Uses the catchupGetLastViewedTime GraphQL query — if lastViewedTimeISO is absent or
 * unparseable, the user has never viewed the page before (isFirstVisit = true).
 */
export function useNudgeFirstVisitFilter(
	contentId: string,
	skip?: boolean,
): UseNudgeFirstVisitFilterResult {
	const { data, loading } = useQueryInterop<
		useNudgeFirstVisitFilterQuery$data,
		useNudgeFirstVisitFilterQuery$variables
	>(useNudgeFirstVisitFilterQuery, {
		variables: { contentId, contentType: 'PAGE' },
		skip: !contentId || skip,
		errorPolicy: 'all',
	});

	const lastViewedTimeISO = data?.catchupGetLastViewedTime?.lastViewedTimeISO ?? '';
	const isFirstVisit = deriveIsFirstVisit(lastViewedTimeISO);

	return useMemo(() => ({ isFirstVisit, loading }), [isFirstVisit, loading]);
}
